import {
    Component,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { InputComponent } from "@components/input/input.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { Observable, of, Subject, takeUntil } from "rxjs";
import { Mottagningsplatstyper } from "~app/common/models/constants";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { TypeaheadService } from "~app/common/services/typeahead/typeahead.service";
import { TypeAhead } from "~app/common/services/typeahead/typeahead.types";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { MottagningsplatsDetaljerTexts } from "~app/features/forsaljning/mottagningsplats/mottagningsplats-detaljer/mottagningsplats-detaljer.texts";
import { MottagningsplatsDetaljerPlatsValidation } from "~app/features/forsaljning/mottagningsplats/mottagningsplats-detaljer/mottagningsplats-detaljer.validation";
import { ILand, IMottagningsplats } from "~app/features/forsaljning/mottagningsplats/mottagningsplats.models";
import { MottagningsplatsService } from "~app/features/forsaljning/mottagningsplats/mottagningsplats.service";

@Component({
    selector: "rask-mottagningsplats-detaljer-header",
    templateUrl: "./mottagningsplats-detaljer-header.component.html",
    styleUrls: ["./mottagningsplats-detaljer-header.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        SelectorComponent,
        CheckboxComponent,
        TextareaComponent
    ]
})
export class MottagningsplatsDetaljerHeaderComponent implements OnInit, OnDestroy, OnChanges {
    form: UntypedFormGroup;
    initialized = false;
    @Input() isNew: boolean;
    @Input() mottagningsplats: IMottagningsplats;
    mottagningsplatsTyper: string[];
    @Input() rootForm: UntypedFormGroup;
    texts = MottagningsplatsDetaljerTexts.texts;
    validation: IValidationScope;
    validationContext: IValidationContext;

    private readonly formBinder = inject(FormBinderService);
    private readonly mottagningsplatsService = inject(MottagningsplatsService);
    private readonly ngFormSubscription$ = new Subject<void>();
    private readonly typeaheadService = inject(TypeaheadService);

    aktivMetodDisabled = (l) => !l.aktiv;

    landDisabled = (item: ILand) => !item.kod;

    landValueFormatter = (item: ILand) => item;

    landViewFormatter = (item: ILand) => `${item?.namn} ${item?.kod || ""}`;

    ngOnChanges() {
        this.setUpForm();
    }

    ngOnDestroy(): void {
        this.formBinder.detachFromRootForm(this.rootForm, `mottagningsplatsut${this.mottagningsplats.id}`);
        this.ngFormSubscription$?.next();
        this.ngFormSubscription$?.complete();
    }

    ngOnInit(): void {
        this.mottagningsplatsTyper = Mottagningsplatstyper.Alla;
        this.setUpForm();
        this.initialized = true;
    }

    searchLandTypeAhead = (input: string) => input.length < 2
        ? this.getPreload(TypeAhead.Land, "Ange minst två tecken")
        : this.mottagningsplatsService.searchLander(input);

    searchLogistikerTypeAhead = (input: string) => input.length < 3
        ? this.getPreload(TypeAhead.AnsvarigLogistiker, this.texts.typaheadefaulttext)
        : this.mottagningsplatsService.searchForAnsvarigLogistikerTypeahead(input);

    setUpForm() {
        this.ngFormSubscription$?.next();

        this.form = this.formBinder.CreateGroup(this.mottagningsplats);
        this.validationContext = MottagningsplatsDetaljerPlatsValidation.initHeader(this.mottagningsplats);
        this.validationContext.scope(ValidationScopes.spara)
            .pipe(takeUntil(this.ngFormSubscription$))
            .subscribe(v => {
                this.validation = v;
            });
        this.validationContext.validate();
        this.formBinder.attachToRootForm(this.rootForm, this.form, `mottagningsplatsut${this.mottagningsplats.id}`).subscribe(x => {
            this.formBinder.attachValidation(this.validationContext.current, this.form, true);
            this.initialized = true;
        });
    }

    typeAheadValueFormatter = (item) => item;

    typeAheadViewFormatter = (item) => item?.namn;

    private getPreload<T>(type: string, text: string): Observable<T[]> {
        return of(this.typeaheadService.getPreload<T>(type, text));
    }
}
